import React, { useMemo, useState } from 'react';

import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { GridApi } from 'ag-grid-community';
import { MssPivotGridData } from 'src/hooks/analytics';

import { MetricsGrid } from '../../MetricsGrid';
import { getUniqueMonths, transformMssPivotGridToPivot } from '../../utils/dataTransformation';
import { createUmgMssColDefs, umgMssColDefsEmpty } from './umgMssColDefs';

export interface UmgMssViewProps {
    filterText?: string;
    data?: MssPivotGridData[];
    loading?: boolean;
    error?: string;
    isDataFullyLoaded?: boolean;
    completionPercentage?: number;
    selectedMonths?: string[];
}

export const UmgMssView: React.FC<UmgMssViewProps> = ({
    filterText = '',
    data = [],
    loading = false,
    error,
    isDataFullyLoaded = true,
    completionPercentage = 100,
    selectedMonths = []
}) => {
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    // Transform filtered data to pivot format
    const pivotData = useMemo(() => transformMssPivotGridToPivot(data), [data]);

    // Get unique months for column definitions
    const availableMonths = useMemo(() => getUniqueMonths(data), [data]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            field: 'territory',
            minWidth: 250,
            pinned: true
        };
    }, []);

    // Generate column definitions based on available months and toggle state
    const columnDefs = useMemo(() => {
        return availableMonths.length > 0 ? createUmgMssColDefs(availableMonths) : umgMssColDefsEmpty;
    }, [availableMonths]);

    // Generate default sort model for UMG MSS view (sort by first month's Standard MSS% column descending)
    const defaultSortModel = useMemo(() => {
        if (availableMonths.length > 0) {
            // Sort by the first (most recent) month's Standard MSS% column in descending order
            const firstMonth = availableMonths[0];
            return [{ colId: `${firstMonth}_standardMss`, sort: 'desc' as const }];
        }
        return [];
    }, [availableMonths]);

    // Handle grid ready event
    const handleGridReady = (api: GridApi) => {
        setAgGridApi(api);
    };

    // Show no data state
    if (isDataFullyLoaded && data.length === 0) {
        return (
            <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                <Text>No UMG MSS data available</Text>
            </Column>
        );
    }

    return (
        <Column heights={['fit', 'fill']}>
            {!isDataFullyLoaded && (
                <Row width="100%" alignmentHorizontal="center" spacing="200">
                    <Text type="b100" color="secondary">
                        Loading UMG data... ({completionPercentage}%)
                    </Text>
                </Row>
            )}

            <MetricsGrid
                columnDefs={columnDefs}
                rowData={pivotData}
                filterText={filterText}
                onGridReady={handleGridReady}
                defaultSortModel={defaultSortModel}
                autoGroupColumnDef={autoGroupColumnDef}
                isDataFullyLoaded={isDataFullyLoaded}
                groupDefaultExpanded={1}
            />
        </Column>
    );
};
